import type { ClientError } from '@tribeplatform/gql-client/lib'
import type {
  Action,
  Collection,
  MutationOrganizeSpacesInCollectionArgs,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  type UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import {
  getCollectionKey,
  getCollectionsKey,
} from '../../utils/keys/collection.key.js'
import { getSpacesKey } from '../../utils/keys/space.key.js'

export const useOrganizeSpacesInCollection = (options?: {
  useMutationOptions?: UseMutationOptions<
    Action,
    ClientError,
    MutationOrganizeSpacesInCollectionArgs,
    { snapshot: Collection }
  >
}) => {
  const { useMutationOptions } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()

  return useMutation<
    Action,
    ClientError,
    MutationOrganizeSpacesInCollectionArgs,
    { snapshot: Collection }
  >(
    (input: MutationOrganizeSpacesInCollectionArgs) =>
      client.collections.organizeSpaces(input),
    {
      onSettled: (data, error, { collectionId }) => {
        const spacesKey = getSpacesKey({ variables: { collectionId } })
        queryClient.invalidateQueries(spacesKey)

        const collectionKey = getCollectionKey({
          variables: { id: collectionId },
        })

        queryClient.invalidateQueries(collectionKey)

        const collectionsKey = getCollectionsKey()
        queryClient.invalidateQueries(collectionsKey)
      },
      ...useMutationOptions,
    },
  )
}
